import { Controller } from 'stimulus'
import {
  stackedColumns, hBars, kpiTile, renderTable, fmtNum, fmtDate,
  groupedColumns, multiLine, hBarsStacked, SLOTS
} from '../lib/metrics/charts'

const GROUP_NAMES = ['Group A', 'Group B', 'Group C']

const STATUS_COLORS = {
  Pass: 'var(--m-st-pass)',
  Fail: 'var(--m-st-fail)',
  Completed: 'var(--m-st-completed)',
  'Not tested': 'var(--m-st-nt)',
  Other: 'var(--m-st-other)'
}

// Drives the Tasks & Results tab. Lazy-loads on first tab activation
// (metrics:results-shown, dispatched by the metrics controller).
export default class extends Controller {
  static targets = [
    'range', 'task', 'traitPicker', 'valuePicker', 'chips',
    'jobFilterPicker', 'jobValuePicker',
    'kpiResults', 'kpiPassRate', 'kpiFails', 'kpiLast',
    'weeklyChart', 'weeklyLegend', 'weeklyTable',
    'hotChart', 'hotTable', 'hotSub', 'byTask',
    'compareBtn', 'groupRows', 'compareGrid', 'singleGrid',
    'cmpTiles', 'cmpLegend', 'cmpWeekly', 'cmpPass', 'cmpMix'
  ];

  connect() {
    this.traitFilters = []
    this.jobChips = []
    this.groups = []
    this.initFromUrl()
    // reload on every show: cheap behind the server-side response cache, and
    // charts re-measure — widths captured while hidden or at another window
    // size would otherwise stick
    this.onShow = () => this.load()
    window.addEventListener('metrics:results-shown', this.onShow)
    // Direct ?tab=results navigation (saved views, shared URLs): the outer
    // metrics controller activates this panel during ITS connect(), which
    // runs before this listener registers — the shown event is already
    // gone. The active class survives, so load from it.
    if (this.element.classList.contains('active')) this.load()
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.element.offsetParent !== null) this.load()
      }, 160)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  // Honor filters arriving via URL (saved-view links, shared URLs)
  initFromUrl() {
    const url = new URLSearchParams(window.location.search)
    if (url.get('tab') !== 'results') return
    if (url.get('range')) this.rangeTarget.value = url.get('range')
    if (url.get('task')) this.taskTarget.value = url.get('task')
    url.getAll('traits[]').forEach((raw) => {
      const [rawKey, value] = raw.split(/:(.*)/s)
      const traitId = decodeURIComponent(rawKey || '')
      if (!traitId || !value) return
      const opt = [...this.traitPickerTarget.options].find(o => o.value === traitId)
      this.traitFilters.push({ traitId, traitName: opt ? opt.textContent : traitId, value })
    })
    url.getAll('tags[]').forEach((value) => {
      if (value) this.jobChips.push({ kind: 'tag', key: '', label: 'Tag', value })
    })
    url.getAll('columns[]').forEach((raw) => {
      const [id, value] = raw.split(/:(.*)/s)
      if (!id || !value) return
      this.jobChips.push({ kind: 'col', key: id, label: this.jobChipLabel(`col:${id}`, `Column #${id}`), value })
    })
    url.getAll('fields[]').forEach((raw) => {
      const [rawKey, value] = raw.split(/:(.*)/s)
      const key = decodeURIComponent(rawKey || '')
      if (!key || !value) return
      this.jobChips.push({ kind: 'field', key, label: this.jobChipLabel(`field:${key}`, key), value })
    })
    if (this.traitFilters.length || this.jobChips.length) this.renderChips()
    const rawGroups = url.getAll('groups[]')
    if (rawGroups.length >= 2) {
      this.groups = rawGroups.slice(0, 3).map((raw, slot) => ({
        slot,
        filters: raw.split('|').filter(Boolean).map((pair) => {
          const [rawKey, value] = pair.split(/:(.*)/s)
          const traitId = decodeURIComponent(rawKey || '')
          const opt = [...this.traitPickerTarget.options].find(o => o.value === traitId)
          return { traitId, traitName: opt ? opt.textContent : traitId, value }
        })
      }))
      this.enterCompare()
    }
  }

  // Captured into saved views (page_name metrics#show).
  viewParams() {
    const params = { tab: 'results', range: this.rangeTarget.value }
    if (this.taskTarget.value) params.task = this.taskTarget.value
    if (this.traitFilters.length) params.traits = this.traitFilters.map(f => `${encodeURIComponent(f.traitId)}:${f.value}`)
    const tags = this.jobChips.filter(c => c.kind === 'tag').map(c => c.value)
    if (tags.length) params.tags = tags
    const columns = this.jobChips.filter(c => c.kind === 'col').map(c => `${c.key}:${c.value}`)
    if (columns.length) params.columns = columns
    const fields = this.jobChips.filter(c => c.kind === 'field').map(c => `${encodeURIComponent(c.key)}:${c.value}`)
    if (fields.length) params.fields = fields
    if (this.compareActive()) {
      params.groups = this.groups.map(g => g.filters.map(f => `${encodeURIComponent(f.traitId)}:${f.value}`).join('|'))
    }
    return params
  }

  disconnect() {
    window.removeEventListener('metrics:results-shown', this.onShow)
    window.removeEventListener('resize', this.resizeHandler)
  }

  queryString() {
    const p = new URLSearchParams({ range: this.rangeTarget.value })
    if (this.taskTarget.value) p.set('task', this.taskTarget.value)
    this.traitFilters.forEach(f => p.append('traits[]', `${encodeURIComponent(f.traitId)}:${f.value}`))
    this.appendJobChipParams(p)
    return p.toString()
  }

  load() {
    this.loadSeq = (this.loadSeq || 0) + 1
    if (this.compareActive()) {
      this.loadCompare()
      return
    }
    const qs = this.queryString()
    this.fetchJson(`${this.data.get('kpisUrl')}?${qs}`, data => this.renderKpis(data))
    this.fetchJson(`${this.data.get('weeklyUrl')}?${qs}`, data => this.renderWeekly(data))
    this.fetchJson(`${this.data.get('hotSpotsUrl')}?${qs}`, data => this.renderHotSpots(data))
    this.fetchJson(`${this.data.get('byTaskUrl')}?${qs}`, data => this.renderByTask(data))
  }

  // ---- comparison groups: N sets of trait filters layered on the shared
  // filters; the same endpoints are fetched once per group in parallel ----

  compareActive() {
    return this.groups.length >= 2
  }

  startCompare() {
    this.groups = [{ slot: 0, filters: [] }, { slot: 1, filters: [] }]
    this.enterCompare()
    this.load()
  }

  enterCompare() {
    this.renderGroupRows()
    this.singleGridTarget.style.display = 'none'
    this.compareGridTarget.style.display = 'grid'
    this.compareBtnTarget.style.display = 'none'
  }

  exitCompare() {
    this.groups = []
    this.groupRowsTarget.innerHTML = ''
    this.singleGridTarget.style.display = 'grid'
    this.compareGridTarget.style.display = 'none'
    this.compareBtnTarget.style.display = ''
    this.load()
  }

  groupSummary(group) {
    return group.filters.map(f => `${f.traitName}: ${f.value}`).join(' · ') || 'All matching results'
  }

  renderGroupRows() {
    const host = this.groupRowsTarget
    host.innerHTML = ''
    this.groups.forEach((group, gi) => {
      const row = document.createElement('div')
      row.className = 'm-grp-row'
      const dot = document.createElement('span')
      dot.className = 'm-gdot'
      dot.style.background = SLOTS[group.slot]
      row.appendChild(dot)
      const name = document.createElement('span')
      name.className = 'm-gname'
      name.textContent = GROUP_NAMES[group.slot]
      row.appendChild(name)

      group.filters.forEach((f, fi) => {
        const chip = document.createElement('span')
        chip.className = 'm-chip'
        const label = document.createElement('span')
        label.textContent = `${f.traitName}: `
        const strong = document.createElement('strong')
        strong.textContent = f.value
        label.appendChild(strong)
        chip.appendChild(label)
        const x = document.createElement('button')
        x.type = 'button'
        x.textContent = '✕'
        x.title = 'Remove filter'
        x.addEventListener('click', () => {
          group.filters.splice(fi, 1)
          this.renderGroupRows()
          this.load()
        })
        chip.appendChild(x)
        row.appendChild(chip)
      })

      const traitSel = document.createElement('select')
      traitSel.appendChild(new Option('+ Add trait…', ''));
      [...this.traitPickerTarget.options].slice(1).forEach((o) => {
        traitSel.appendChild(new Option(o.textContent, o.value))
      })
      const valueSel = document.createElement('select')
      valueSel.appendChild(new Option('value…', ''))
      valueSel.disabled = true
      traitSel.addEventListener('change', () => {
        valueSel.innerHTML = ''
        valueSel.appendChild(new Option('Loading…', ''))
        valueSel.disabled = true
        if (!traitSel.value) return
        this.fetchJson(`${this.data.get('traitValuesUrl')}?${new URLSearchParams({ trait: traitSel.value })}`, (data) => {
          valueSel.innerHTML = ''
          valueSel.appendChild(new Option('value…', ''))
          data.values.forEach(v => valueSel.appendChild(new Option(v, v)))
          valueSel.disabled = false
        }, false)
      })
      valueSel.addEventListener('change', () => {
        if (!traitSel.value || !valueSel.value) return
        group.filters.push({
          traitId: traitSel.value,
          traitName: traitSel.selectedOptions[0].textContent,
          value: valueSel.value
        })
        this.renderGroupRows()
        this.load()
      })
      row.appendChild(traitSel)
      row.appendChild(valueSel)

      if (this.groups.length > 2) {
        const rm = document.createElement('button')
        rm.type = 'button'
        rm.className = 'm-btn'
        rm.textContent = '✕ Remove group'
        rm.addEventListener('click', () => {
          this.groups.splice(gi, 1)
          this.groups.forEach((g, i) => { Object.assign(g, { slot: i }) })
          this.renderGroupRows()
          this.load()
        })
        row.appendChild(rm)
      }
      host.appendChild(row)
    })

    const actions = document.createElement('div')
    actions.className = 'm-grp-actions'
    if (this.groups.length < 3) {
      const add = document.createElement('button')
      add.type = 'button'
      add.className = 'm-btn'
      add.textContent = '+ Add comparison group'
      add.addEventListener('click', () => {
        this.groups.push({ slot: this.groups.length, filters: [] })
        this.renderGroupRows()
        this.load()
      })
      actions.appendChild(add)
    }
    const exit = document.createElement('button')
    exit.type = 'button'
    exit.className = 'm-btn'
    exit.textContent = 'Exit compare'
    exit.addEventListener('click', () => this.exitCompare())
    actions.appendChild(exit)
    host.appendChild(actions)
  }

  groupQueryString(group) {
    const p = new URLSearchParams({ range: this.rangeTarget.value })
    if (this.taskTarget.value) p.set('task', this.taskTarget.value)
    this.traitFilters.forEach(f => p.append('traits[]', `${encodeURIComponent(f.traitId)}:${f.value}`))
    this.appendJobChipParams(p)
    group.filters.forEach(f => p.append('traits[]', `${encodeURIComponent(f.traitId)}:${f.value}`))
    return p.toString()
  }

  loadCompare() {
    const seq = this.loadSeq
    const fetches = this.groups.map(group => Promise.all([
      fetch(`${this.data.get('kpisUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
      fetch(`${this.data.get('weeklyUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json())
    ]))
    Promise.all(fetches).then((results) => {
      if (seq !== this.loadSeq) return
      this.renderCompare(results)
    }).catch(() => {})
  }

  renderCompare(results) {
    const groups = this.groups.map((group, i) => ({
      group,
      kpis: results[i][0],
      weekly: results[i][1],
      color: SLOTS[group.slot],
      name: GROUP_NAMES[group.slot]
    }))
    const { weeks } = groups[0].weekly

    const seriesSum = (weekly, label, i) => {
      const s = weekly.series.find(x => x.label === label)
      return s ? (s.values[i] || 0) : 0
    }

    // per-group pass-rate tiles
    const tiles = this.cmpTilesTarget
    tiles.innerHTML = ''
    groups.forEach((g) => {
      const tile = document.createElement('div')
      tile.className = 'm-card m-cmp-tile'
      kpiTile(tile, {
        label: `${g.name} — pass rate`,
        dot: g.color,
        value: g.kpis.current.pass_pct === null ? '—' : `${g.kpis.current.pass_pct}%`,
        delta: null,
        deltaText: null,
        sub: `${this.groupSummary(g.group)} · ${fmtNum(g.kpis.current.results)} results · ${fmtNum(g.kpis.current.fails)} fail`.slice(0, 90)
      })
      tiles.appendChild(tile)
    })

    // legend
    const legend = this.cmpLegendTarget
    legend.innerHTML = ''
    groups.forEach((g) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = g.color
      key.appendChild(sw)
      key.appendChild(document.createTextNode(`${g.name}: ${this.groupSummary(g.group)}`.slice(0, 60)))
      legend.appendChild(key)
    })

    // clustered weekly totals, pass rate in tooltip
    groupedColumns(this.cmpWeeklyTarget, {
      weeks,
      groups: groups.map(g => ({
        label: g.name,
        color: g.color,
        values: weeks.map((_, i) => g.weekly.series.reduce((a, s) => a + (s.values[i] || 0), 0)),
        tips: weeks.map((_, i) => {
          const pass = seriesSum(g.weekly, 'Pass', i)
          const fail = seriesSum(g.weekly, 'Fail', i)
          const tested = pass + fail
          return tested ? `${g.name} · ${Math.round((100 * pass) / tested)}% pass` : g.name
        })
      }))
    })

    // rolling 4-week pass rate
    multiLine(this.cmpPassTarget, {
      weeks,
      series: groups.map(g => ({
        label: g.name,
        color: g.color,
        points: weeks.map((_, i) => {
          let pass = 0; let fail = 0
          for (let k = Math.max(0, i - 3); k <= i; k += 1) {
            pass += seriesSum(g.weekly, 'Pass', k)
            fail += seriesSum(g.weekly, 'Fail', k)
          }
          const tested = pass + fail
          return tested >= 3 ? Math.round((100 * pass) / tested) : null
        })
      }))
    })

    // status mix per group
    const statusColors = {
      Pass: 'var(--m-st-pass)', Fail: 'var(--m-st-fail)', Completed: 'var(--m-st-completed)', 'Not tested': 'var(--m-st-nt)', Other: 'var(--m-st-other)'
    }
    hBarsStacked(this.cmpMixTarget, groups.map(g => ({
      label: g.name,
      total: g.kpis.current.results,
      parts: Object.keys(statusColors).map(label => ({
        label,
        color: statusColors[label],
        value: weeks.reduce((a, _, i) => a + seriesSum(g.weekly, label, i), 0)
      }))
    })))
  }

  // Guarded responses belonging to a superseded load() are dropped, so a
  // slow older request can't paint over a newer one. Picker fetches pass
  // guarded: false — they populate dropdowns, not cards.
  fetchJson(url, onData, guarded = true) {
    const seq = this.loadSeq || 0
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => {
        if (guarded && seq !== (this.loadSeq || 0)) return
        onData(data)
      })
      .catch(() => {})
  }

  // ---- trait filter chips ----

  traitChosen() {
    const traitId = this.traitPickerTarget.value
    const picker = this.valuePickerTarget
    picker.innerHTML = ''
    if (!traitId) {
      picker.disabled = true
      picker.appendChild(new Option('Pick a trait first', ''))
      return
    }
    picker.disabled = true
    picker.appendChild(new Option('Loading…', ''))
    this.fetchJson(`${this.data.get('traitValuesUrl')}?${new URLSearchParams({ trait: traitId })}`, (data) => {
      picker.innerHTML = ''
      picker.appendChild(new Option('Pick a value…', ''))
      data.values.forEach(v => picker.appendChild(new Option(v, v)))
      picker.disabled = false
    }, false)
  }

  addTrait() {
    const { value } = this.valuePickerTarget
    const traitId = this.traitPickerTarget.value
    if (!value || !traitId) return
    const traitName = this.traitPickerTarget.selectedOptions[0].textContent
    this.pushTraitFilter({ traitId, traitName, value })
    this.traitPickerTarget.value = ''
    this.traitChosen()
  }

  pushTraitFilter(filter) {
    const exists = this.traitFilters.some(f => f.traitId === String(filter.traitId) && f.value === filter.value)
    if (!exists) this.traitFilters.push({ ...filter, traitId: String(filter.traitId) })
    this.renderChips()
    this.load()
  }

  removeTrait(ev) {
    this.traitFilters.splice(Number(ev.currentTarget.dataset.index), 1)
    this.renderChips()
    this.load()
  }

  // ---- job-level filter chips (tag / job column / job field) — scope
  // the jobs whose results are counted; server-side twin of the Jobs tab
  // chips ----

  jobChipLabel(sourceValue, fallback) {
    const opt = [...this.jobFilterPickerTarget.options].find(o => o.value === sourceValue)
    return opt ? opt.textContent : fallback
  }

  jobChipValuesUrl(source) {
    if (source === 'tag') return this.data.get('tagValuesUrl')
    const [kind, key] = source.split(/:(.*)/s)
    return kind === 'col'
      ? `${this.data.get('columnValuesUrl')}?column_id=${key}`
      : `${this.data.get('fieldValuesUrl')}?${new URLSearchParams({ field: key })}`
  }

  jobFilterChosen() {
    const source = this.jobFilterPickerTarget.value
    const picker = this.jobValuePickerTarget
    picker.innerHTML = ''
    if (!source) {
      picker.disabled = true
      picker.appendChild(new Option('Pick a filter first', ''))
      return
    }
    picker.disabled = true
    picker.appendChild(new Option('Loading…', ''))
    this.fetchJson(this.jobChipValuesUrl(source), (data) => {
      picker.innerHTML = ''
      picker.appendChild(new Option('Pick a value…', ''))
      data.values.forEach(v => picker.appendChild(new Option(v, v)))
      picker.disabled = false
    }, false)
  }

  addJobChip() {
    const { value } = this.jobValuePickerTarget
    const source = this.jobFilterPickerTarget.value
    if (!source || !value) return
    const [kind, key] = source === 'tag' ? ['tag', ''] : source.split(/:(.*)/s)
    const label = source === 'tag' ? 'Tag' : this.jobFilterPickerTarget.selectedOptions[0].textContent
    const exists = this.jobChips.some(c => c.kind === kind && c.key === key && c.value === value)
    if (!exists) this.jobChips.push({ kind, key, label, value })
    this.jobFilterPickerTarget.value = ''
    this.jobFilterChosen()
    this.renderChips()
    this.load()
  }

  removeJobChip(ev) {
    this.jobChips.splice(Number(ev.currentTarget.dataset.index), 1)
    this.renderChips()
    this.load()
  }

  appendJobChipParams(p) {
    this.jobChips.forEach((c) => {
      if (c.kind === 'tag') p.append('tags[]', c.value)
      else if (c.kind === 'col') p.append('columns[]', `${c.key}:${c.value}`)
      else p.append('fields[]', `${encodeURIComponent(c.key)}:${c.value}`)
    })
  }

  renderChips() {
    const host = this.chipsTarget
    host.innerHTML = ''
    const appendChip = (labelText, value, index, action) => {
      const chip = document.createElement('span')
      chip.className = 'm-chip'
      const label = document.createElement('span')
      label.textContent = `${labelText}: `
      const strong = document.createElement('strong')
      strong.textContent = value
      label.appendChild(strong)
      chip.appendChild(label)
      const x = document.createElement('button')
      x.type = 'button'
      x.textContent = '✕'
      x.title = 'Remove filter'
      x.dataset.index = index
      x.dataset.action = action
      chip.appendChild(x)
      host.appendChild(chip)
    }
    this.traitFilters.forEach((f, i) => appendChip(f.traitName, f.value, i, 'click->metrics-results#removeTrait'))
    this.jobChips.forEach((c, i) => appendChip(c.label, c.value, i, 'click->metrics-results#removeJobChip'))
  }

  // ---- cards ----

  renderKpis(data) {
    const cur = data.current
    const prev = data.previous
    const pct = (a, b) => (b ? Math.round(((a - b) / b) * 100) : null)
    const resultsDelta = pct(cur.results, prev.results)
    kpiTile(this.kpiResultsTarget, {
      label: 'Results',
      value: fmtNum(cur.results),
      delta: resultsDelta,
      deltaText: resultsDelta === null ? null : `${Math.abs(resultsDelta)}%`,
      sub: 'one per task × sample'
    })
    const prDelta = (cur.pass_pct !== null && prev.pass_pct !== null) ? cur.pass_pct - prev.pass_pct : null
    kpiTile(this.kpiPassRateTarget, {
      label: 'Pass rate',
      value: cur.pass_pct === null ? '—' : `${cur.pass_pct}%`,
      delta: prDelta,
      deltaText: prDelta === null ? null : `${Math.abs(prDelta)} pts`,
      sub: `of ${fmtNum(cur.tested)} tested results`
    })
    const failDelta = pct(cur.fails, prev.fails)
    kpiTile(this.kpiFailsTarget, {
      label: 'Failures',
      value: fmtNum(cur.fails),
      delta: failDelta,
      deltaText: failDelta === null ? null : `${Math.abs(failDelta)}%`,
      upIsGood: false
    })
    kpiTile(this.kpiLastTarget, {
      label: 'Last result',
      value: data.last ? this.relative(data.last.at) : '—',
      delta: null,
      deltaText: null,
      sub: data.last ? `${data.last.job} · ${data.last.task}`.slice(0, 60) : 'No matching results'
    })
  }

  relative(iso) {
    const days = Math.round((Date.now() - new Date(iso).getTime()) / 86400000)
    if (days <= 0) return 'today'
    if (days === 1) return 'yesterday'
    return `${days} d ago`
  }

  renderWeekly(data) {
    const series = stackedColumns(this.weeklyChartTarget, data, { colorMap: STATUS_COLORS })
    const legend = this.weeklyLegendTarget
    legend.innerHTML = ''
    if (series && series.length > 1) {
      series.forEach((s) => {
        const key = document.createElement('span')
        key.className = 'm-key'
        const sw = document.createElement('span')
        sw.className = 'm-swatch'
        sw.style.background = s.color
        key.appendChild(sw)
        key.appendChild(document.createTextNode(s.label))
        legend.appendChild(key)
      })
    }
    renderTable(
      this.weeklyTableTarget,
      [{ label: 'Week' }].concat(data.series.map(s => ({ label: s.label, num: true }))),
      data.weeks.map((w, i) => [`Week of ${fmtDate(w)}`].concat(data.series.map(s => fmtNum(s.values[i] || 0)))),
    )
  }

  renderHotSpots(data) {
    this.hotSubTarget.textContent = `Highest fail rate · min ${data.min_tested} tested${
      data.overall_fail_pct === null ? '' : ` · overall ${data.overall_fail_pct}%`
    } · click to add as a filter`
    hBars(this.hotChartTarget, data.rows.map(r => ({
      label: `${r.trait}: ${r.value}`,
      value: r.fail_pct,
      tipExtra: `${fmtNum(r.fails)} of ${fmtNum(r.tested)} tested failed`,
      traitId: r.trait_key,
      traitName: r.trait,
      rawValue: r.value
    })), {
      color: 'var(--m-st-fail)',
      labelW: 190,
      unitLabel: 'fail rate',
      fmt: v => `${v}%`,
      clickHint: 'Click to filter to this trait value',
      onClick: row => this.pushTraitFilter({ traitId: row.traitId, traitName: row.traitName, value: row.rawValue })
    })
    renderTable(
      this.hotTableTarget,
      [{ label: 'Trait value' }, { label: 'Fail rate', num: true }, { label: 'Failed / tested', num: true }],
      data.rows.map(r => [`${r.trait}: ${r.value}`, `${r.fail_pct}%`, `${fmtNum(r.fails)} / ${fmtNum(r.tested)}`]),
    )
  }

  renderByTask(data) {
    const host = this.byTaskTarget
    host.innerHTML = ''
    if (!data.rows.length) {
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = 'No results for these filters.'
      host.appendChild(empty)
      return
    }
    const taskKeys = new Set([...this.taskTarget.options].map(o => o.value))
    const table = document.createElement('table')
    table.className = 'm-table'
    const thead = document.createElement('thead')
    const trh = document.createElement('tr')
    const cols = [{ label: 'Task' }, { label: 'Results', num: true }, { label: 'Last result', num: true }, { label: 'Pass rate' }]
    cols.forEach((c) => {
      const th = document.createElement('th')
      th.textContent = c.label
      if (c.num) th.className = 'num'
      trh.appendChild(th)
    })
    thead.appendChild(trh)
    table.appendChild(thead)
    const tbody = document.createElement('tbody')
    data.rows.forEach((r) => {
      const tr = document.createElement('tr')
      const cell = (text, num) => {
        const td = document.createElement('td')
        td.textContent = text
        if (num) td.className = 'num'
        tr.appendChild(td)
        return td
      }
      cell(r.name)
      cell(fmtNum(r.results), true)
      cell(fmtDate(r.last_at), true)
      const td = document.createElement('td')
      if (r.pass_pct === null) {
        td.textContent = '—'
      } else {
        const meter = document.createElement('span')
        meter.className = 'm-meter'
        const track = document.createElement('span')
        track.className = 'm-meter-track'
        const fill = document.createElement('span')
        fill.className = `m-meter-fill${r.pass_pct < 80 ? ' low' : ''}`
        fill.style.width = `${r.pass_pct}%`
        track.appendChild(fill)
        meter.appendChild(track)
        const pctLabel = document.createElement('span')
        pctLabel.textContent = `${r.pass_pct}%`
        meter.appendChild(pctLabel)
        td.appendChild(meter)
      }
      tr.appendChild(td)
      if (taskKeys.has(r.task_key)) {
        tr.className = 'm-rowlink'
        tr.addEventListener('click', () => {
          this.taskTarget.value = r.task_key
          this.load()
        })
      }
      tbody.appendChild(tr)
    })
    table.appendChild(tbody)
    host.appendChild(table)
  }

  toggleTable(ev) {
    const btn = ev.currentTarget
    const card = btn.closest('.m-card')
    const showing = card.classList.toggle('show-table')
    btn.textContent = showing ? 'Chart' : 'Table'
  }
}
